import { nextTick, watch } from 'vue';

interface FlipItem {
  id: string;
}

// Manual FLIP for a v-for'd list keyed by item id. Consumers call armFlip()
// before a mutation that may reorder `items`, then bind each row's element via
// setRowRef - the animation plays only if the order actually changes, never
// on mount/unrelated updates.
export const useFlipReorderAnimation = <T extends FlipItem>(items: () => T[]) => {
  const rowEls = new Map<string, HTMLElement>();

  const setRowRef = (id: string | undefined, el: unknown) => {
    if (!id) return;
    const node = (el as { $el?: HTMLElement } | null)?.$el ?? (el as HTMLElement | null);
    if (node) rowEls.set(id, node);
    else rowEls.delete(id);
  };

  const captureRects = () => {
    const rects = new Map<string, number>();
    for (const [id, el] of rowEls) rects.set(id, el.getBoundingClientRect().top);
    return rects;
  };

  const playFlip = (prevRects: Map<string, number>) => {
    for (const [id, el] of rowEls) {
      const prevTop = prevRects.get(id);
      if (prevTop == null) continue;
      const delta = prevTop - el.getBoundingClientRect().top;
      if (!delta) continue;

      el.style.transition = 'none';
      el.style.transform = `translateY(${delta}px)`;
      requestAnimationFrame(() => {
        el.style.transition = 'transform 700ms cubic-bezier(0.34, 1.56, 0.64, 1)';
        el.style.transform = '';
      });
    }
  };

  let pendingFlipRects: Map<string, number> | null = null;

  const armFlip = () => {
    const rects = captureRects();
    pendingFlipRects = rects;

    // Safety valve: if the request fails and items never changes, don't let
    // this stale capture corrupt a later, unrelated reorder's animation.
    setTimeout(() => {
      if (pendingFlipRects === rects) pendingFlipRects = null;
    }, 3000);
  };

  watch(items, async (newItems, oldItems) => {
    if (!pendingFlipRects) return;
    const orderChanged = newItems.some((item, i) => item.id !== oldItems[i]?.id);
    if (!orderChanged) return;
    const rects = pendingFlipRects;
    pendingFlipRects = null;
    await nextTick();
    playFlip(rects);
  });

  return { setRowRef, armFlip };
};
